import { useMemo } from 'react';
import { useQuery, focusManager } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { useAuth } from '@/auth/hooks/useAuth';

export interface ArcheBadge {
  count: number;
  mentionCount: number;
}

/** Poll cadence while the tab is visible. */
const FOREGROUND_POLL_MS = 45_000;
/**
 * Poll cadence while the tab is hidden (only when `pollInBackground`). Backed
 * off 4× from the foreground rate: a hidden tab still needs to advance the
 * count for the cross-tab indicator, but at full rate every logged-in user's
 * idle background tab would hammer `/notifications/summary` (2–4 DB queries per
 * call) forever. This caps that steady-state load.
 */
const BACKGROUND_POLL_MS = 180_000;

export interface UseNotificationSummaryOptions {
  /**
   * Keep polling while the tab is hidden/backgrounded. Default `false` — the
   * header badge and worktop only need to be fresh when the user can see them.
   * The cross-tab unread indicator (`useUnreadIndicator`) opts in so the total
   * keeps advancing while Fias is in the background, which is what lets the
   * tab/favicon update and the flash fire while the user is focused elsewhere.
   * When enabled, the hidden-tab cadence is throttled (`BACKGROUND_POLL_MS`).
   *
   * All observers share one cache entry (same query key), so enabling this on
   * the always-mounted indicator also keeps the header badge fresh. The cost is
   * that a second observer of the same key runs its own interval, so while
   * visible the summary endpoint may be hit up to twice per foreground tick;
   * this is intentional and bounded. The scalable long-term path is WebSocket
   * push instead of polling — tracked separately.
   */
  pollInBackground?: boolean;
}

/**
 * Cross-arche notification badge summary for the header + worktop.
 *
 * Polls every ~45s while the tab is visible and refetches on window focus
 * (global default). By default polling pauses while the tab is hidden; pass
 * `pollInBackground` to keep it running. Local actions that change counts —
 * e.g. reading a chat — should invalidate `queryKeys.notifications.summary()`
 * to update immediately instead of waiting for the poll.
 *
 * Disabled when logged out, so the header (which renders for unauthenticated
 * users) never fires the request.
 */
export function useNotificationSummary({
  pollInBackground = false,
}: UseNotificationSummaryOptions = {}) {
  const { isAuthenticated } = useAuth();

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.notifications.summary(),
    queryFn: () => api.getNotificationSummary(),
    enabled: isAuthenticated,
    // Foreground always polls at 45s; when background polling is enabled we
    // throttle to a slower cadence while the tab is hidden.
    refetchInterval: pollInBackground
      ? () => (focusManager.isFocused() ? FOREGROUND_POLL_MS : BACKGROUND_POLL_MS)
      : FOREGROUND_POLL_MS,
    refetchIntervalInBackground: pollInBackground,
    staleTime: 30_000,
  });

  const badges = useMemo(() => {
    const map: Record<string, ArcheBadge> = {};
    for (const b of data?.badges ?? []) {
      map[b.archeId] = { count: b.count, mentionCount: b.mentionCount };
    }
    return map;
  }, [data]);

  return {
    /** Per-arche badge lookup keyed by archeId. */
    badges,
    total: data?.total ?? 0,
    totalMentions: data?.totalMentions ?? 0,
    isLoading,
  };
}
